import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FAILURE, PENDING, PRISTINE, SUCCESS,
} from 'utils/constants/queryStatuses';
import { setKeyValue, unsetKeyValue } from './actions';

const INIT_STATE = { status: PRISTINE, data: null };
const DATA_ID_FIELD = '_dataId';

const latestEffectDataIdByStoreKey = {};

const getParametersIdDefault = (params) => params;

const updateStateAction = ({ storeKey, ...rest }) => (dispatch, getState) => {
  const { [DATA_ID_FIELD]: dataId } = rest;
  if (!storeKey || !dataId) {
    // invariant
    return;
  }

  const { keyValueStore: { [storeKey]: { [DATA_ID_FIELD]: currentDataId } = {} } } = getState();
  if (currentDataId && dataId !== currentDataId) {
    // canceled
    return;
  }
  dispatch(setKeyValue(storeKey, rest));
};

export const usePersistedAsyncAction = ({
  storeKey,
  action,
  parameters,
  dataId: dataIdPrefix,
  // to determine if the parameters have changed
  getParametersId = getParametersIdDefault,
  isActive,
}) => {
  const dispatch = useDispatch();
  const abortControllerRef = useRef(null);
  const storeKeyRef = useRef(`persistedAsyncAction/${storeKey}`);
  const requestCounterRef = useRef(0);
  const state = useSelector((rootState) => rootState.keyValueStore[storeKeyRef.current]);

  const dataId = useMemo(() => (
    `${dataIdPrefix}_${getParametersId(parameters)}`
  ), [dataIdPrefix, getParametersId, parameters]);

  const effect = useCallback((signal) => {
    const actionArgs = [{ signal }];
    if (parameters !== undefined) {
      actionArgs.unshift(parameters);
    }
    return action(...actionArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataId]);

  const setState = useCallback((nextState) => {
    dispatch(dispatch(setKeyValue(storeKeyRef.current, { ...nextState, [DATA_ID_FIELD]: dataId })));
  }, [dispatch, dataId]);

  const updateState = useCallback((nextState) => {
    dispatch(updateStateAction({ ...nextState, storeKey: storeKeyRef.current, [DATA_ID_FIELD]: dataId }));
  }, [dispatch, dataId]);

  useEffect(() => {
    if (isActive) {
      return;
    }
    dispatch(unsetKeyValue(storeKeyRef.current));
  }, [dispatch, isActive]);

  const shouldRunEffect = state?.[DATA_ID_FIELD] !== dataId;

  useEffect(() => {
    if (!shouldRunEffect) {
      return;
    }
    if (latestEffectDataIdByStoreKey[storeKey] === dataId) {
      // already in progress; prevent many simultaneous call for the same data id
      return;
    }

    latestEffectDataIdByStoreKey[storeKey] = dataId;

    // abort previous effect if isActive: false
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    requestCounterRef.current += 1;
    const currentRequestNumber = requestCounterRef.current;

    if (!isActive) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    setState({ data: null, status: PENDING });

    effect(signal)
      .then((result) => {
        if (currentRequestNumber !== requestCounterRef.current) {
          return;
        }
        updateState({ data: result, status: SUCCESS });
      })
      .catch((ex) => {
        if (currentRequestNumber !== requestCounterRef.current) {
          return;
        }
        console.error(`useAsyncAction ${dataIdPrefix} ex:`, ex);
        updateState({ data: null, status: FAILURE });
      });
  }, [dataId, dataIdPrefix, effect, isActive, setState, shouldRunEffect, storeKey, updateState]);

  return isActive && state?.[DATA_ID_FIELD] === dataId ? state : INIT_STATE;
};
